import React from 'react'
import { NODE_VISUAL_CONSTANTS } from '@genieartinc/schemas'
import { Icon } from '@/components/ui/icon'
import { UPLOADABLE_AUDIO_LABEL } from './audio-upload'

interface AudioFileEmptyProps {
  isUploading: boolean
  uploadProgress: number | null
  hasError: boolean
  onUpload: () => void
}

/** The dashed upload target shown while an AudioFile node holds no file. */
export function AudioFileEmpty({ isUploading, uploadProgress, hasError, onUpload }: AudioFileEmptyProps) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation()
        onUpload()
      }}
      disabled={isUploading}
      className="flex flex-1 flex-col w-full box-border items-center justify-center gap-3 border-2 border-dashed border-border transition-colors hover:border-muted-foreground hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
      style={{ borderRadius: `${NODE_VISUAL_CONSTANTS.borderRadius.button.large}px` }}
    >
      {isUploading ? (
        <>
          <Icon name="loader-circle" className="h-6 w-6 text-muted-foreground animate-spin" />
          <div className="flex flex-col items-center gap-1">
            <span className="text-sm text-muted-foreground">Uploading...</span>
            {uploadProgress !== null && (
              <span className="text-xs text-muted-foreground">{uploadProgress}%</span>
            )}
          </div>
        </>
      ) : (
        <>
          <Icon name="music" className="h-6 w-6 text-muted-foreground" />
          <div className="flex flex-col items-center gap-1">
            <span className="text-sm text-muted-foreground">
              {hasError ? 'Upload failed. Click to retry' : 'Drag and drop your audio here'}
            </span>
            <span className="text-xs text-muted-foreground">{UPLOADABLE_AUDIO_LABEL}</span>
          </div>
        </>
      )}
    </button>
  )
}

interface AudioFileStatusProps {
  status: 'loading' | 'error'
  message?: string
  progress?: number
}

/** Generation placeholder / failure, matching the other file nodes. */
export function AudioFileStatus({ status, message, progress }: AudioFileStatusProps) {
  if (status === 'error') {
    return (
      <div className="flex flex-1 flex-col w-full items-center justify-center gap-2 rounded-[10px] bg-destructive/10">
        <Icon name="alert-circle" className="h-6 w-6 text-destructive" />
        <span className="text-xs text-destructive">Generation failed</span>
      </div>
    )
  }
  return (
    <div className="flex flex-1 flex-col w-full items-center justify-center gap-3 rounded-[10px] bg-muted animate-pulse" role="status" aria-label="Generating audio content">
      <span className="text-xs font-medium text-muted-foreground">{message || 'Generating audio...'}</span>
      <span className="text-[10px] tabular-nums text-muted-foreground" aria-live="polite">
        {Math.round(progress ?? 0)}%
      </span>
    </div>
  )
}
